import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { CfgDeviceRow, CfgDeviceStatusKind } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { sumWidth, TipText } from '@hq/ui-kit';
import { hq, useAuthStore } from '../session';

const PAGE_SIZE = 20;

const DEVICE_TYPE_META: Record<string, string> = {
  RFID_READER: 'RFID 读写器',
  PDA: '手持 PDA',
  PRINTER: '标签打印机',
  SMART_CABINET: '智能柜',
  WRISTBAND_PRINTER: '腕带打印机'
};

const STATUS_META: Record<string, { text: string; color: string }> = {
  ONLINE: { text: '在线', color: 'green' },
  OFFLINE: { text: '离线', color: 'default' },
  FAULT: { text: '故障', color: 'red' }
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.text }));

interface DeviceFormValues {
  deviceCode: string;
  deviceName: string;
  deviceType: string;
  orgId: string;
  warehouseId?: string;
  bindLocationCode?: string;
  readRate?: number;
  readDistance?: number;
  ip?: string;
  mac?: string;
  status?: CfgDeviceStatusKind;
  providerFree?: boolean;
  remark?: string;
}

function StatusTag({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { text: status || '-', color: 'default' };
  return <Tag color={meta.color}>{meta.text}</Tag>;
}

/**
 * RF 终端设备台账（`cfg_device`）：RFID 读写器 / PDA / 打印机 / 智能柜的建档与运行状态维护。
 * 设备挂在机构与仓库上，读取速率与识别距离供盘点/出入库选型参考（RFID 读写器要求 ≥150 张/秒）。
 */
export default function RfDevices() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  const canManage =
    profile?.level === 'admin' || (profile?.perms ? hasPermission(profile.perms, 'sys:config:manage') : false);

  const [keyword, setKeyword] = useState('');
  const [deviceType, setDeviceType] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CfgDeviceRow | null>(null);
  const [form] = Form.useForm<DeviceFormValues>();

  const list = useInfiniteQuery({
    queryKey: ['ops', 'cfg-device', keyword, deviceType, status, orgId],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.cfgDevice.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        keyword: keyword || undefined,
        deviceType,
        status,
        orgId
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const orgs = useQuery({
    queryKey: ['ops', 'org', 'options'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => hq.org.options({})
  });

  const formOrgId = Form.useWatch('orgId', form);
  const warehouses = useQuery({
    queryKey: ['ops', 'warehouse', 'options', formOrgId],
    enabled: open && Boolean(formOrgId),
    staleTime: 5 * 60 * 1000,
    queryFn: () => hq.orgStructure.warehouse.list({ orgId: formOrgId })
  });

  const save = useMutation({
    mutationFn: (payload: { id?: string; values: DeviceFormValues }) => {
      const values = payload.values;
      return hq.cfgDevice.save({
        id: payload.id,
        deviceCode: values.deviceCode.trim(),
        deviceName: values.deviceName.trim(),
        deviceType: values.deviceType,
        orgId: values.orgId,
        warehouseId: values.warehouseId ?? '0',
        bindLocationCode: values.bindLocationCode?.trim() || null,
        readRate: values.readRate ?? 0,
        readDistance: values.readDistance ?? 0,
        ip: values.ip?.trim() || null,
        mac: values.mac?.trim() || null,
        status: values.status ?? 'OFFLINE',
        providerFree: values.providerFree ?? false,
        remark: values.remark?.trim() || null
      });
    },
    onSuccess: async (result) => {
      message.success(result.created ? '设备已建档' : '设备已更新');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'cfg-device'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const changeStatus = useMutation({
    mutationFn: (payload: { id: string; status: CfgDeviceStatusKind }) => {
      return hq.cfgDevice.setStatus(payload.id, payload.status);
    },
    onSuccess: async () => {
      message.success('状态已更新');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'cfg-device'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '状态更新失败');
    }
  });

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({ deviceType: 'RFID_READER', status: 'OFFLINE', providerFree: false, readRate: 0, readDistance: 0 });
    setOpen(true);
  };

  const openEdit = (row: CfgDeviceRow) => {
    setEditing(row);
    form.setFieldsValue({
      deviceCode: row.deviceCode,
      deviceName: row.deviceName,
      deviceType: row.deviceType,
      orgId: row.orgId,
      warehouseId: row.warehouseId === '0' ? undefined : row.warehouseId,
      bindLocationCode: row.bindLocationCode ?? undefined,
      readRate: row.readRate,
      readDistance: row.readDistance,
      ip: row.ip ?? undefined,
      mac: row.mac ?? undefined,
      status: row.status as CfgDeviceStatusKind,
      providerFree: row.providerFree,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<CfgDeviceRow> = [
    {
      title: '设备编码',
      dataIndex: 'deviceCode',
      width: 160,
      render: (value: string) => <Typography.Text code>{value}</Typography.Text>
    },
    { title: '设备名称', dataIndex: 'deviceName', width: 170, ellipsis: { showTitle: false }, render: (value: string) => <TipText text={value} /> },
    {
      title: '类型',
      dataIndex: 'deviceType',
      width: 130,
      render: (value: string) => <Tag>{DEVICE_TYPE_META[value] ?? value}</Tag>
    },
    { title: '归属机构', dataIndex: 'orgName', width: 150, ellipsis: { showTitle: false }, render: (value: string | null, row) => <TipText text={value || `机构 #${row.orgId}`} /> },
    {
      title: '归属仓库 / 货位',
      key: 'warehouse',
      width: 190,
      render: (_value: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{row.warehouseName || (row.warehouseId === '0' ? '未指定仓库' : `仓库 #${row.warehouseId}`)}</Typography.Text>
          <Typography.Text type="secondary">{row.bindLocationCode ? `货位 ${row.bindLocationCode}` : '未绑定货位'}</Typography.Text>
        </Space>
      )
    },
    {
      title: '读取速率(张/秒)',
      dataIndex: 'readRate',
      width: 130,
      align: 'right',
      render: (value: number, row) => {
        if (row.deviceType !== 'RFID_READER') return value > 0 ? value : '-';
        return <Typography.Text type={value > 0 && value < 150 ? 'danger' : undefined}>{value || '-'}</Typography.Text>;
      }
    },
    {
      title: '识别距离(m)',
      dataIndex: 'readDistance',
      width: 110,
      align: 'right',
      render: (value: number) => (value > 0 ? value : '-')
    },
    {
      title: '网络',
      key: 'network',
      width: 180,
      render: (_value: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{row.ip || 'IP 未登记'}</Typography.Text>
          <Typography.Text type="secondary">{row.mac || 'MAC 未登记'}</Typography.Text>
        </Space>
      )
    },
    {
      title: '免费提供',
      dataIndex: 'providerFree',
      width: 100,
      render: (value: boolean) => (value ? <Tag color="blue">中标方提供</Tag> : '-')
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 120,
      render: (value: string, row) =>
        canManage ? (
          <Select
            size="small"
            style={{ width: 92 }}
            value={value}
            options={STATUS_OPTIONS}
            loading={changeStatus.isPending}
            onChange={(next) => changeStatus.mutate({ id: row.id, status: next as CfgDeviceStatusKind })}
          />
        ) : (
          <StatusTag status={value} />
        )
    },
    { title: '备注', dataIndex: 'remark', width: 160, ellipsis: { showTitle: false }, render: (value: string | null) => <TipText text={value || '-'} /> },
    { title: '更新时间', dataIndex: 'updateAt', width: 170, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_value: unknown, row) =>
        canManage ? (
          <Button type="link" onClick={() => openEdit(row)}>
            编辑
          </Button>
        ) : (
          <Typography.Text type="secondary">只读</Typography.Text>
        )
    }
  ];

  return (
    <Card className="page-card" title="RF 终端设备">
      <Typography.Paragraph type="secondary">
        设备台账（`cfg_device`）：RFID 读写器 / PDA / 标签打印机 / 智能柜 / 腕带打印机。设备绑定到机构与仓库（可再绑定货位），
        读取速率与识别距离供盘点、出入库选型参考；RFID 读写器要求读取速率 ≥150 张/秒。建档与状态维护需 `sys:config:manage`。
      </Typography.Paragraph>

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="按编码/名称/IP/MAC 搜索"
          style={{ width: 240 }}
          onSearch={(value) => setKeyword(value.trim())}
          enterButton
        />
        <Select
          allowClear
          placeholder="按类型筛选"
          style={{ width: 160 }}
          value={deviceType}
          onChange={(next) => setDeviceType(next)}
          options={Object.entries(DEVICE_TYPE_META).map(([value, label]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="按状态筛选"
          style={{ width: 130 }}
          value={status}
          onChange={(next) => setStatus(next)}
          options={STATUS_OPTIONS}
        />
        <Select
          allowClear
          placeholder="按机构筛选"
          style={{ width: 200 }}
          value={orgId}
          onChange={(next) => setOrgId(next)}
          options={(orgs.data ?? []).map((row) => ({ value: row.id, label: row.orgName }))}
        />
        <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
          刷新
        </Button>
        {canManage ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建设备
          </Button>
        ) : null}
      </Space>

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="设备列表加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<CfgDeviceRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无设备' }}
        onRow={(row) => ({ onDoubleClick: canManage ? () => openEdit(row) : undefined })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Modal
        title={editing ? `维护设备：${editing.deviceCode}` : '新建设备'}
        open={open}
        width={760}
        destroyOnHidden
        confirmLoading={save.isPending}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={async () => {
          const values = await form.validateFields();
          save.mutate({ id: editing?.id, values });
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="deviceCode"
                label="设备编码"
                rules={[
                  { required: true, message: '请填写设备编码' },
                  { pattern: /^[0-9A-Za-z_-]{2,64}$/, message: '2–64 位字母、数字、下划线或短横线' }
                ]}
              >
                <Input maxLength={64} placeholder="如：RF-WH01-001（全局唯一）" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="deviceName"
                label="设备名称"
                rules={[{ required: true, message: '请填写设备名称' }, { max: 128, message: '不超过 128 字' }]}
              >
                <Input maxLength={128} placeholder="如：中心仓入库口读写器" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="deviceType" label="设备类型" rules={[{ required: true, message: '请选择设备类型' }]}>
                <Select options={Object.entries(DEVICE_TYPE_META).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="运行状态">
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="orgId" label="归属机构" rules={[{ required: true, message: '请选择归属机构' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="选择机构"
                  options={(orgs.data ?? []).map((row) => ({ value: row.id, label: row.orgName }))}
                  onChange={() => form.setFieldsValue({ warehouseId: undefined })}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="warehouseId" label="归属仓库" extra="可选；仓库须属所选机构">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={formOrgId ? '选择仓库' : '先选择机构'}
                  disabled={!formOrgId}
                  loading={warehouses.isFetching}
                  options={(warehouses.data ?? []).map((row) => ({ value: row.id, label: `${row.whName}（${row.whCode}）` }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="bindLocationCode" label="安装货位" rules={[{ max: 32, message: '不超过 32 字' }]}>
                <Input maxLength={32} placeholder="如：A-01-03" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="readRate" label="读取速率(张/秒)" extra="RFID 读写器要求 ≥150，其它类型可填 0">
                <InputNumber min={0} max={100000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="readDistance" label="识别距离(m)">
                <InputNumber min={0} max={1000} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="ip" label="IP" rules={[{ max: 64, message: '不超过 64 字' }]}>
                <Input maxLength={64} placeholder="如：192.168.10.21" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mac" label="MAC" rules={[{ max: 64, message: '不超过 64 字' }]}>
                <Input maxLength={64} placeholder="如：AC:12:34:56:78:90" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="providerFree" label="中标方免费提供" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remark" label="备注" rules={[{ max: 500, message: '不超过 500 字' }]}>
            <Input.TextArea rows={2} maxLength={500} showCount placeholder="安装位置、维护责任等" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
